// components/consumers/documents/DocumentsTab.tsx

import { useConsumerDocuments } from "@features/cx/consumerManagement/hooks";
import { Document } from "@features/cx/consumerManagement/types";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { SortConfig, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Upload } from "lucide-react";
import React, { useMemo, useState } from "react";
import { UploadDocumentModal } from "./UploadDocumentModal";

interface DocumentsTabProps {
  consumerId: string;
  consumerNo: string;
  remoteUtilityId: number;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { data, isLoading, error } = useConsumerDocuments(consumerId, {
    page: currentPage,
    limit: PAGE_SIZE,
    query: searchTerm,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    remote_utility_id: remoteUtilityId,
  });
  // Flatten the paginated response data
  const flattenedDocuments = useMemo(() => {
    if (!data?.results) return [];
    return data.results;
  }, [data]);

  // Define table columns
  const columns: TableColumn<Document>[] = [
    {
      key: "documentType",
      header: "Type",
    },
    {
      key: "documentSubtype",
      header: "Subtype"
    },
    {
      key: "referenceId",
      header: "Reference ID"
    },
    {
      key: "createdDate",
      header: "Upload Date",
      render: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Verified":
              return "default";
            case "Pending":
              return "secondary";
            case "Rejected":
              return "outline";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Verified":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "Pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            case "Rejected":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "createdUserRemoteName",
      header: "Uploaded By",
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Updated By",
    },
  ];

  // Table actions for each row
  const actions = [
    {
      label: "View",
      icon: "Eye" as const,
      onClick: (row: Document, index: number) => {
        const link = document.createElement("a");
        link.href = row.file;
        link.target = "_blank";
        link.download = row.file.split("/").pop() || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      variant: "ghost" as const,
    },
  ];

  // Handle sorting
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleSelectionChange = (newSelectedRows: string[]) => {
    setSelectedRows(newSelectedRows);
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <Button
          onClick={() => {
            setCurrentPage(1);
          }}
          className="mt-2"
          variant="outline"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with PageHeader */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader
            title="Account Documents"
            subtitle="View account documents, attached contracts, and upload additional files."
          />
        </div>
        <div>
          <Button variant="default" onClick={() => setIsUploadModalOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>
      {/* Generic Table */}
      <GenericTable
        data={flattenedDocuments}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        pagination={{
          currentPage: data?.page || currentPage || 1,
          totalPages: data?.total_pages || Math.ceil((data?.count || 0) / (data?.limit || 10)) || 1,
          totalItems: data?.count || flattenedDocuments.length || 0,
          itemsPerPage: data?.limit || 10,
          startItem: (data?.count || flattenedDocuments.length) ? ((data?.page || currentPage || 1) - 1) * (data?.limit || 10) + 1 : 0,
          endItem: (data?.count || flattenedDocuments.length)
            ? Math.min((data?.page || currentPage || 1) * (data?.limit || 10), data?.count || flattenedDocuments.length || 0)
            : 0,
        }}
        onPageChange={handlePageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchPlaceholder="Search documents..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No documents found"
      />

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={(data) => {
          console.log("Uploaded document data:", data);
          // Handle document upload here (e.g., call API)
          setIsUploadModalOpen(false);
        }}
      />
    </div>
  );
};
